import { ScopeReportChangeType } from '@kaiizen/kai-pro-api';
import { GroupedDiffLineItems, GroupedReportLineItems, ScopeReportLineItem, ScopeReportLineItemWithChangeData } from '../ScopeReportTypes';

/**
 * Available filter types for the attention summary
 */
export type AttentionSummaryFilterType =
  | 'editRequests'
  | 'changeOrders'
  | 'unassignedContractors'
  | 'deferredTasks'
  | 'tenantResp'
  | 'outOfStock';

/**
 * Filters sowData to only include edit request items
 */
export const filterEditRequestGroups = (sowData: GroupedReportLineItems): GroupedReportLineItems => {
  const filtered: GroupedReportLineItems = {};
  Object.entries(sowData).forEach(([groupName, items]) => {
    const editRequestItems = items.filter(item => item.isEditRequest === true);
    if (editRequestItems.length > 0) {
      filtered[groupName] = editRequestItems;
    }
  });
  return filtered;
};

/**
 * Builds a composite key for uniquely identifying a line item
 */
export const buildLineItemKey = (groupId: string, taskId: string, optionId: string, lineItemId: string): string =>
  `${groupId}|${taskId}|${optionId}|${lineItemId}`;

/** @deprecated use ScopeReportChangeType instead */
export type ChangeType = ScopeReportChangeType;

/**
 * Builds a Map of line item keys to their change types from an array of diff line items
 */
export const buildChangeOrderLookupFromLineItems = (
  diffLineItems?: ScopeReportLineItemWithChangeData[],
): Map<string, ScopeReportChangeType> | undefined => {
  if (!diffLineItems || diffLineItems.length === 0) return new Map<string, ScopeReportChangeType>();

  const changeOrderMap = new Map<string, ScopeReportChangeType>();
  diffLineItems.forEach(item => {
    const key = buildLineItemKey(item.groupId, item.taskId, item.optionId, item.lineItemId);
    const changeType = item.changeOrderData?.changeType ?? 'existing';
    changeOrderMap.set(key, changeType);
  });

  return changeOrderMap.size > 0 ? changeOrderMap : undefined;
};

/**
 * Builds a Set of line item keys from grouped diff line items
 */
const buildChangeOrderKeysFromGroupedLineItems = (
  diffLineItems?: GroupedDiffLineItems | null,
): Set<string> | null => {
  if (!diffLineItems) return null;

  const keys = new Set<string>();
  Object.values(diffLineItems).forEach(items => {
    items.forEach(item => {
      const key = buildLineItemKey(item.groupId, item.taskId, item.optionId, item.lineItemId);
      keys.add(key);
    });
  });

  return keys.size > 0 ? keys : null;
};

/**
 * Filters sowData to only include unassigned contractor items
 */
export const filterUnassignedContractorGroups = (sowData: GroupedReportLineItems): GroupedReportLineItems => {
  const filtered: GroupedReportLineItems = {};
  Object.entries(sowData).forEach(([groupName, items]) => {
    const unassignedItems = items.filter(item => item.contractorName === 'Unassigned');
    if (unassignedItems.length > 0) {
      filtered[groupName] = unassignedItems;
    }
  });
  return filtered;
};

const itemMatchesAttentionFilter = (
  item: ScopeReportLineItem,
  activeFilters: Set<AttentionSummaryFilterType>,
  changeOrderKeys: Set<string> | null,
): boolean => {
  if (activeFilters.has('editRequests') && item.isEditRequest === true && item.isEditResolution === false) return true;
  if (activeFilters.has('unassignedContractors') && item.contractorName === 'Unassigned') return true;
  if (activeFilters.has('deferredTasks') && item.isDeferredLineItem === true) return true;
  if (activeFilters.has('tenantResp') && item.isTenantRespLineItem === true) return true;
  if (activeFilters.has('outOfStock') && item.productExists === false) return true;
  if (activeFilters.has('changeOrders') && changeOrderKeys) {
    const key = buildLineItemKey(item.groupId, item.taskId, item.optionId, item.lineItemId);
    return changeOrderKeys.has(key);
  }
  return false;
};

/**
 * Applies attention summary filters to a flat list of line items.
 * When multiple filters are active, items must match ANY of the filters (OR logic).
 */
export const applyAttentionSummaryFiltersToLineItems = (
  lineItems: ScopeReportLineItem[],
  activeFilters: Set<AttentionSummaryFilterType>,
  diffLineItems?: ScopeReportLineItemWithChangeData[] | null,
): ScopeReportLineItem[] => {
  if (activeFilters.size === 0) return lineItems;

  const changeOrderKeys = activeFilters.has('changeOrders') && diffLineItems && diffLineItems.length > 0
    ? new Set(
      diffLineItems.map(item => buildLineItemKey(item.groupId, item.taskId, item.optionId, item.lineItemId)),
    )
    : null;

  return lineItems.filter(item => itemMatchesAttentionFilter(item, activeFilters, changeOrderKeys));
};

/**
 * Applies multiple filters to sowData based on active filter set
 * When multiple filters are active, items must match ANY of the filters (OR logic)
 */
export const applyAttentionSummaryFilters = (
  sowData: GroupedReportLineItems,
  activeFilters: Set<AttentionSummaryFilterType>,
  diffLineItems?: GroupedDiffLineItems | null,
): GroupedReportLineItems => {
  if (activeFilters.size === 0) {
    return sowData;
  }

  const changeOrderKeys = activeFilters.has('changeOrders')
    ? buildChangeOrderKeysFromGroupedLineItems(diffLineItems)
    : null;

  const filtered: GroupedReportLineItems = {};

  Object.entries(sowData).forEach(([groupName, items]) => {
    const matchingItems = items.filter(item => itemMatchesAttentionFilter(item, activeFilters, changeOrderKeys));

    if (matchingItems.length > 0) {
      filtered[groupName] = matchingItems;
    }
  });

  return filtered;
};
